 

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import "../styles/StackVisualizer.css"
import Button from "../../shared/Button";

const PLATE_COLORS = ["#FF6B6B", "#4ECDC4", "#FFD166", "#06D6A0", "#118AB2", "#EF476F", "#FFC43D", "#1B9AAA"]

function StackVisualizer({ addLog, currentStep }) {
  const [stack, setStack] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipContent, setTooltipContent] = useState("")
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [showPopup, setShowPopup] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")

  // For guided demo
  useEffect(() => {
    if (currentStep === null) return

    // Reset stack for demo
    if (currentStep === 0) {
      setStack([])
      addLog("Initialized empty stack", "system")
    } else if (currentStep === 1) {
      // First push
      const demoItem = {
        value: "First",
        id: "demo-1",
        color: PLATE_COLORS[0],
      }
      setStack([demoItem])
      addLog("Pushed 'First' onto the stack", "system")
    } else if (currentStep === 2) {
      // Second push
      const demoItems = [
        {
          value: "Second",
          id: "demo-2",
          color: PLATE_COLORS[1],
        },
        {
          value: "First",
          id: "demo-1",
          color: PLATE_COLORS[0],
        },
      ]
      setStack(demoItems)
      addLog("Pushed 'Second' onto the stack", "system")
    } else if (currentStep === 3) {
      // Pop operation
      const demoItems = [
        {
          value: "First",
          id: "demo-1",
          color: PLATE_COLORS[0],
        },
      ]
      setStack(demoItems)
      addLog("Popped 'Second' from the stack", "system")
    } else if (currentStep === 4) {
      // Final state
      const demoItems = [
        {
          value: "First",
          id: "demo-1",
          color: PLATE_COLORS[0],
        },
      ]
      setStack(demoItems)
      addLog("Current stack state shown", "system")
    }
  }, [currentStep, addLog])

  const handlePush = () => {
    if (inputValue.trim() === "") return
    if (stack.length >= 8) {
      addLog("⚠️ Stack Overflow! Cannot add more items.", "error")
      setPopupMessage("No more empty space! Stack is full.")
      setShowPopup(true)
      setTimeout(() => setShowPopup(false), 3000)
      return
    }

    setIsAnimating(true)

    // Generate a random color for the new plate
    const randomColor = PLATE_COLORS[Math.floor(Math.random() * PLATE_COLORS.length)]

    const newItem = {
      value: inputValue,
      id: Date.now(),
      color: randomColor,
    }

    addLog(`Pushing "${inputValue}" onto the stack`, "success")
    setStack((prevStack) => [newItem, ...prevStack])
    setInputValue("")

    setTimeout(() => {
      setIsAnimating(false)
    }, 500)
  }

  const handlePop = () => {
    if (stack.length === 0) {
      addLog("⚠️ Stack Underflow! Cannot remove from an empty stack.", "error")
      return
    }

    setIsAnimating(true)
    addLog(`Popping "${stack[0].value}" from the stack`, "info")

    setTimeout(() => {
      setStack((prevStack) => prevStack.slice(1))
      setIsAnimating(false)
    }, 500)
  }

  const handleClear = () => {
    setStack([])
    addLog("Stack cleared", "warning")
  }

  const handleMouseEnter = (index, item) => {
    setHighlightedIndex(index)
    setShowTooltip(true)
    setTooltipContent(`Value: ${item.value}, Position: ${index + 1}`)
  }

  const handleMouseMove = (e) => {
    setTooltipPosition({ x: e.clientX, y: e.clientY })
  }

  const handleMouseLeave = () => {
    setHighlightedIndex(null)
    setShowTooltip(false)
  }

  return (
    <div className="stack-container">
      <div className="stack-controls">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter a value"
            disabled={isAnimating || currentStep !== null}
            className="stack-input"
            onKeyDown={(e) => { if (e.key === "Enter") handlePush() }}
          />
          <div className="stack-buttons">
            <Button onClick={handlePush} disabled={isAnimating || inputValue.trim() === "" || currentStep !== null}>Push</Button>
            <Button variant="secondary" onClick={handlePop} disabled={isAnimating || stack.length === 0 || currentStep !== null}>Pop</Button>
            <Button variant="danger" onClick={handleClear} disabled={isAnimating || stack.length === 0 || currentStep !== null}>Clear</Button>
          </div>
        </div>
      </div>

      <div className="stack-visualization">
        <div className="stack-labels">
          <div className="stack-top-label">Top</div>
          <div className="stack-bottom-label">Bottom</div>
        </div>

        <div className="stack-structure">
          <div className="stack-base">
            <div className="stack-pole-left"></div>
            <div className="stack-pole-right"></div>
          </div>

          <div className="plates-container">
            <AnimatePresence>
              {stack.map((item, index) => (
                <motion.div
                  key={item.id}
                  className={`plate ${highlightedIndex === index ? "highlighted" : ""}`}
                  style={{
                    backgroundColor: item.color,
                    zIndex: stack.length - index,
                  }}
                  initial={{ y: -100, opacity: 0, scale: 0.8 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: -100, opacity: 0, scale: 0.8, transition: { duration: 0.3 } }}
                  transition={{
                    duration: 0.5,
                    type: "spring",
                    stiffness: 200,
                    damping: 20,
                  }}
                  onMouseEnter={() => handleMouseEnter(index, item)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  whileHover={{ scale: 1.05 }}
                >
                  <span className="plate-value">{item.value}</span>
                  <span className="plate-index">{index}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {showTooltip && (
          <div
            className="tooltip"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y + 10,
            }}
          >
            {tooltipContent}
          </div>
        )}

        {showPopup && (
          <motion.div
            className="popup-message"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {popupMessage}
          </motion.div>
        )}
      </div>

      <div className="stack-info">
        <div className="stack-status">
          <div className="status-item">
            <span className="status-label">Size:</span>
            <span className="status-value">{stack.length}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Is Empty:</span>
            <span className="status-value">{stack.length === 0 ? "Yes" : "No"}</span>
          </div>
          <div className="status-item">
            <span className="status-label">Is Full:</span>
            <span className="status-value">{stack.length >= 8 ? "Yes" : "No"}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StackVisualizer
